import { useState, useEffect, useRef, useCallback } from 'react'
import { whatsAppAPI, metaAssetsAPI, whatsAppNumbersAPI } from '../services/api'
import { Spinner, Alert, Select, Modal, Button } from '../components/ui'

const WhatsAppPage = () => {
  const [selectedWhatsAppNumber, setSelectedWhatsAppNumber] = useState(null)
  const [whatsAppNumbers, setWhatsAppNumbers] = useState([])
  const [phoneNumber, setPhoneNumber] = useState('')
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [fromUserId, setFromUserId] = useState(null) // Store the WhatsApp number owner's user ID
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingNumbers, setLoadingNumbers] = useState(false)
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const messagesEndRef = useRef(null)
  const chatContainerRef = useRef(null)

  // Load WhatsApp numbers and restore state on component mount
  useEffect(() => {
    const initializePage = async () => {
      // Load WhatsApp numbers first
      const numbers = await loadWhatsAppNumbers()
      
      // Try to restore selected WhatsApp number from localStorage
      const storedWhatsAppNumber = localStorage.getItem('selected_whatsapp_number')
      const storedWhatsAppUser = localStorage.getItem('selected_whatsapp_user')
      
      if (storedWhatsAppNumber && storedWhatsAppUser && numbers.length > 0) {
        try {
          const numberData = JSON.parse(storedWhatsAppNumber)
          const userData = JSON.parse(storedWhatsAppUser)
          
          // Find the WhatsApp number in the loaded list
          const foundNumber = numbers.find(num => num.id === numberData.id)
          
          if (foundNumber) {
            // Restore the selected WhatsApp number
            setSelectedWhatsAppNumber(foundNumber)
            const phoneNum = foundNumber.whatsapp_number.replace(/^91/, '')
            setPhoneNumber(phoneNum)
            setFromUserId(userData.id)
            
            // Load users for this number
            await loadUsersForNumber(phoneNum)
            
            // Note: selected user will be restored in loadUsersForNumber
          }
        } catch (err) {
          console.error('Failed to restore WhatsApp number:', err)
          localStorage.removeItem('selected_whatsapp_number')
          localStorage.removeItem('selected_whatsapp_user')
          localStorage.removeItem('selected_chat_user')
        }
      }
    }
    
    initializePage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const loadWhatsAppNumbers = async () => {
    try {
      setLoadingNumbers(true)
      setError(null)
      const response = await whatsAppNumbersAPI.getAll()
      
      if (response.data.success) {
        const numbers = response.data.data || []
        setWhatsAppNumbers(numbers)
        return numbers
      } else {
        setError(response.data.message || 'Failed to load WhatsApp numbers')
        return []
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to load WhatsApp numbers. Please try again.'
      )
      setWhatsAppNumbers([])
      return []
    } finally {
      setLoadingNumbers(false)
    }
  }

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Handle WhatsApp number selection
  const handleWhatsAppNumberChange = async (e) => {
    const selectedId = e.target.value
    if (!selectedId) {
      setSelectedWhatsAppNumber(null)
      setPhoneNumber('')
      setUsers([])
      setSelectedUser(null)
      setFromUserId(null)
      setMessages([])
      localStorage.removeItem('selected_whatsapp_number')
      localStorage.removeItem('selected_whatsapp_user')
      localStorage.removeItem('selected_chat_user')
      return
    }

    const selected = whatsAppNumbers.find(num => num.id.toString() === selectedId)
    if (!selected) return

    setSelectedWhatsAppNumber(selected)
    
    // Store WhatsApp number and whatsapp_user in localStorage
    localStorage.setItem('selected_whatsapp_number', JSON.stringify({
      id: selected.id,
      whatsapp_number: selected.whatsapp_number,
      display_name: selected.display_name
    }))
    
    if (selected.whatsapp_user) {
      localStorage.setItem('selected_whatsapp_user', JSON.stringify(selected.whatsapp_user))
      setFromUserId(selected.whatsapp_user.id)
    } else {
      localStorage.removeItem('selected_whatsapp_user')
      setFromUserId(null)
    }
    
    // Extract phone number without country code (remove '91' prefix if present)
    const phoneNum = selected.whatsapp_number.replace(/^91/, '')
    setPhoneNumber(phoneNum)

    // Automatically load users for the selected WhatsApp number
    await loadUsersForNumber(phoneNum)
  }

  // Load users when WhatsApp number is selected
  const loadUsersForNumber = async (phoneNum) => {
    if (!phoneNum.trim()) {
      setError('Please select a WhatsApp number')
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await whatsAppAPI.getUsers(phoneNum)
      
      if (response.data.success) {
        const userList = response.data.data || []
        setUsers(userList)
        
        // fromUserId should already be set from localStorage or handleWhatsAppNumberChange
        // If not set, try to get it from stored WhatsApp user
        if (!fromUserId) {
          const storedWhatsAppUser = localStorage.getItem('selected_whatsapp_user')
          if (storedWhatsAppUser) {
            try {
              const user = JSON.parse(storedWhatsAppUser)
              setFromUserId(user.id)
            } catch (err) {
              console.error('Failed to parse stored WhatsApp user:', err)
            }
          }
        }
        
        if (userList.length > 0) {
          // Try to restore selected user from localStorage
          const storedSelectedUser = localStorage.getItem('selected_chat_user')
          if (storedSelectedUser) {
            try {
              const storedUser = JSON.parse(storedSelectedUser)
              const foundUser = userList.find(u => u.id === storedUser.id)
              if (foundUser) {
                setSelectedUser(foundUser)
                // Store updated user info
                localStorage.setItem('selected_chat_user', JSON.stringify({
                  id: foundUser.id,
                  user_mobile_number: foundUser.user_mobile_number,
                  name: foundUser.name
                }))
                return // Don't select first user if we restored one
              }
            } catch (err) {
              console.error('Failed to parse stored selected user:', err)
            }
          }
          
          // Select the first user if no stored user found
          const firstUser = userList[0]
          setSelectedUser(firstUser)
          // Store first user in localStorage
          localStorage.setItem('selected_chat_user', JSON.stringify({
            id: firstUser.id,
            user_mobile_number: firstUser.user_mobile_number,
            name: firstUser.name
          }))
        } else {
          setSelectedUser(null)
          setMessages([])
          localStorage.removeItem('selected_chat_user')
        }
      } else {
        setError(response.data.message || 'Failed to load users')
        setUsers([])
        setSelectedUser(null)
        setMessages([])
        localStorage.removeItem('selected_chat_user')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to load users. Please check your WhatsApp number.'
      )
      setUsers([])
      setSelectedUser(null)
      setMessages([])
      localStorage.removeItem('selected_chat_user')
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = useCallback(async () => {
    if (!selectedUser || !phoneNumber) return

    try {
      setLoading(true)
      setError(null)
      
      // Get from_user_id from localStorage or state
      let currentFromUserId = fromUserId
      
      // If not in state, try to get from localStorage
      if (!currentFromUserId) {
        const storedWhatsAppUser = localStorage.getItem('selected_whatsapp_user')
        if (storedWhatsAppUser) {
          try {
            const user = JSON.parse(storedWhatsAppUser)
            currentFromUserId = user.id
            setFromUserId(user.id)
          } catch (err) {
            console.error('Failed to parse stored WhatsApp user:', err)
          }
        }
      }
      
      // If still no fromUserId, show error
      if (!currentFromUserId) {
        setError('WhatsApp user not found. Please select a WhatsApp number first.')
        setMessages([])
        return
      }
      
      const response = await whatsAppAPI.getUserMessages(
        currentFromUserId,
        selectedUser.id
      )
      
      if (response.data.success) {
        setMessages(response.data.data?.chat || [])
        
        // Verify from_user_id matches (optional validation)
        if (response.data.data?.from_user) {
          const apiFromUserId = response.data.data.from_user.id
          // Update localStorage if the ID is different (shouldn't happen, but just in case)
          if (apiFromUserId !== currentFromUserId) {
            const storedWhatsAppUser = localStorage.getItem('selected_whatsapp_user')
            if (storedWhatsAppUser) {
              try {
                const user = JSON.parse(storedWhatsAppUser)
                user.id = apiFromUserId
                localStorage.setItem('selected_whatsapp_user', JSON.stringify(user))
                setFromUserId(apiFromUserId)
              } catch (err) {
                console.error('Failed to update stored WhatsApp user:', err)
              }
            }
          }
        }
      } else {
        setError(response.data.message || 'Failed to load messages')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to load messages'
      )
      setMessages([])
    } finally {
      setLoading(false)
    }
  }, [selectedUser, phoneNumber, fromUserId])

  // Load messages when a user is selected
  useEffect(() => {
    if (selectedUser && phoneNumber) {
      loadMessages()
    }
  }, [selectedUser, phoneNumber, loadMessages])

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !selectedUser || !fromUserId) return

    try {
      setSending(true)
      setError(null)
      
      const response = await whatsAppAPI.sendMessage(
        fromUserId,
        selectedUser.id,
        newMessage.trim()
      )
      
      if (response.data.success) {
        setNewMessage('')
        // Reload messages to show the new one
        await loadMessages()
      } else {
        setError(response.data.message || 'Failed to send message')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to send message'
      )
    } finally {
      setSending(false)
    }
  }

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    })
  }

  // Format phone number by removing country code prefix
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return ''
    // Remove '91' prefix if present
    return phoneNumber.replace(/^91/, '')
  }

  // Get display name for user (name or formatted phone number)
  const getUserDisplayName = (user) => {
    if (user.name) return user.name
    return formatPhoneNumber(user.user_mobile_number)
  }

  // Check if there are messages for today
  const hasMessagesToday = () => {
    if (!messages || messages.length === 0) return false
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    return messages.some(message => {
      const messageDate = new Date(message.created_at)
      messageDate.setHours(0, 0, 0, 0)
      return messageDate.getTime() === today.getTime()
    })
  }

  // Determine if message is sent by from_user (right side) or received (left side)
  const isMessageFromUser = (message) => {
    return message.from_user_id === fromUserId
  }

  // Prepare options for the dropdown
  const whatsAppNumberOptions = whatsAppNumbers.map(num => ({
    value: num.id.toString(),
    label: `${num.display_name} (${num.whatsapp_number})`
  }))

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col bg-gray-100">
      {/* WhatsApp Number Selection - Show when no WhatsApp number is selected */}
      {!selectedWhatsAppNumber && (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">WhatsApp</h2>
              <p className="text-gray-600">Select your WhatsApp number to start chatting</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Select
                  id="whatsapp-number"
                  label="WhatsApp Number"
                  options={whatsAppNumberOptions}
                  placeholder={loadingNumbers ? "Loading WhatsApp numbers..." : "Select a WhatsApp number"}
                  value={selectedWhatsAppNumber?.id?.toString() || ''}
                  onChange={handleWhatsAppNumberChange}
                  disabled={loadingNumbers || whatsAppNumbers.length === 0}
                  required
                />
                {loadingNumbers && (
                  <div className="mt-2 flex items-center justify-center">
                    <Spinner size="sm" />
                    <span className="ml-2 text-sm text-gray-500">Loading WhatsApp numbers...</span>
                  </div>
                )}
                {!loadingNumbers && whatsAppNumbers.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No WhatsApp numbers available</p>
                )}
              </div>
              
              {error && (
                <Alert variant="danger" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Interface */}
      {selectedWhatsAppNumber && (
        <div className="flex-1 flex overflow-hidden bg-white">
          {/* Left Sidebar - Chat List */}
          <div className="w-1/3 border-r border-gray-300 flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-green-500 text-white p-4 flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">WhatsApp</h3>
                  <p className="text-xs text-green-100">
                    {selectedWhatsAppNumber?.display_name || phoneNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedWhatsAppNumber(null)
                  setPhoneNumber('')
                  setUsers([])
                  setSelectedUser(null)
                  setFromUserId(null)
                  setMessages([])
                  localStorage.removeItem('selected_whatsapp_number')
                  localStorage.removeItem('selected_whatsapp_user')
                  localStorage.removeItem('selected_chat_user')
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                title="Change Number"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-3 bg-white border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search or start new chat"
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Chat List */}
            <div className="flex-1 overflow-y-auto">
              {loading && users.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner size="lg" />
                </div>
              ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                  <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-center">No chats found</p>
                  <p className="text-sm text-center mt-2">Start a conversation to see messages here</p>
                </div>
              ) : (
                users.map((user) => {
                  const displayName = getUserDisplayName(user)
                  const formattedPhone = formatPhoneNumber(user.user_mobile_number)
                  return (
                    <div
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user)
                        // Store selected user in localStorage
                        localStorage.setItem('selected_chat_user', JSON.stringify({
                          id: user.id,
                          user_mobile_number: user.user_mobile_number,
                          name: user.name
                        }))
                      }}
                      className={`p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors ${
                        selectedUser?.id === user.id ? 'bg-gray-100' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {user.name ? user.name.charAt(0).toUpperCase() : formattedPhone.slice(-1)}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-800 truncate">
                              {displayName}
                            </h4>
                          </div>
                          {user.name && (
                            <p className="text-sm text-gray-500 truncate">
                              {formattedPhone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* Right Side - Chat Window */}
          <div className="flex-1 flex flex-col">
            {selectedUser ? (
              <>
                {/* Chat Header */}
                <div className="bg-gray-100 border-b border-gray-300 p-4 flex items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                    {selectedUser.name 
                      ? selectedUser.name.charAt(0).toUpperCase() 
                      : formatPhoneNumber(selectedUser.user_mobile_number).slice(-1)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">
                      {getUserDisplayName(selectedUser)}
                    </h3>
                    {selectedUser.name && (
                      <p className="text-sm text-gray-500">{formatPhoneNumber(selectedUser.user_mobile_number)}</p>
                    )}
                  </div>
                  <button className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                </div>

                {/* Messages */}
                <div
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto p-4 bg-gray-50"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 100 0 L 0 0 0 100\' fill=\'none\' stroke=\'%23e5e7eb\' stroke-width=\'1\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grid)\'/%3E%3C/svg%3E")' }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Spinner size="lg" />
                    </div>
                  ) : messages.length === 0 || !hasMessagesToday() ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p className="text-lg font-medium mb-2">No messages today</p>
                      <p className="text-sm mb-6">Start the conversation by sending a template message</p>
                      <Button
                        variant="primary"
                        onClick={() => setShowTemplateModal(true)}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Initiate Chat
                      </Button>
                    </div>
                  ) : (
                    messages.map((message) => {
                      const isSent = isMessageFromUser(message)
                      return (
                        <div
                          key={message.id}
                          className={`flex mb-4 ${isSent ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              isSent
                                ? 'bg-green-500 text-white rounded-br-none'
                                : 'bg-white text-gray-800 rounded-bl-none shadow-sm'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
                            <p
                              className={`text-xs mt-1 ${
                                isSent ? 'text-green-100' : 'text-gray-500'
                              }`}
                            >
                              {formatMessageTime(message.created_at)}
                              {isSent && (
                                <span className="ml-1">
                                  {message.status === 'sent' ? '✓' : message.status === 'delivered' ? '✓✓' : '✓✓✓'}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      )
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="bg-gray-100 border-t border-gray-300 p-4">
                  {error && (
                    <div className="mb-2">
                      <Alert variant="danger" onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    </div>
                  )}
                  <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message"
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={sending || !fromUserId}
                    />
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim() || !fromUserId}
                      className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? (
                        <Spinner size="sm" />
                      ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      )}
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-xl font-medium">Select a chat to start messaging</p>
                  <p className="text-sm mt-2">Choose a conversation from the list</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Message Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Send Template Message"
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowTemplateModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                // TODO: Implement template message sending
                setShowTemplateModal(false)
              }}
              className="bg-green-500 hover:bg-green-600"
            >
              Send Template
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Name
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="">Select a template</option>
              <option value="hello_world">Hello World</option>
              <option value="job_registration_invite">Job Registration Invite</option>
              <option value="welcome_message">Welcome Message</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="es">Spanish</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Parameters (Optional)
            </label>
            <textarea
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              rows="4"
              placeholder='Enter parameters as JSON, e.g., {"name": "John"}'
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter template parameters in JSON format if required by the template
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Template messages are required to initiate conversations with users who haven&apos;t messaged you in the last 24 hours.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default WhatsAppPage

